// src/services/contractsService.js
// Генерация договоров «вместо водителя» (порт documentGenerator.js) + загрузка подписанного PDF.
import { collection, getDocs, setDoc, doc, Timestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { prepareReplacementData, replacePlaceholders } from './documentHelpers';
import { generatePDFFromHTML } from './pdfGenerator';

const REQUIRED_DOCUMENTS = ['RODO', 'Umowa najmu', 'Zasady współpracy'];

const wrapHtml = (processedHtml) => `
  <!DOCTYPE html>
  <html lang="pl">
  <head>
    <meta charset="UTF-8">
    <style>
      @page { size: A4; margin: 20mm; }
      body { font-family: Arial, sans-serif; font-size: 10pt; line-height: 1.5; color: #333; }
      .signature-line { border-bottom: 1px dotted #000; width: 300px; margin-top: 40px; display: inline-block; }
      .page-break { page-break-after: always; }
    </style>
  </head>
  <body>${processedHtml}</body>
  </html>
`;

// Генерирует RODO / Umowa najmu / Zasady współpracy в users/{userId}/umowy
export const generateDocumentsForUser = async ({ firestore, storage, userId, firm }) => {
  const templatesSnap = await getDocs(collection(firestore, 'documentTemplates'));
  if (templatesSnap.empty) return { ok: false, generated: 0, reason: 'no_templates' };

  const replacements = await prepareReplacementData(firestore, userId, firm);

  const results = await Promise.all(templatesSnap.docs.map(async (templateDoc) => {
    const template = templateDoc.data();
    if (!REQUIRED_DOCUMENTS.includes(template.name)) return false;
    const htmlContent = template.content;
    if (!htmlContent) return false;

    const processedHtml = replacePlaceholders(htmlContent, replacements);
    const pdfBlob = await generatePDFFromHTML(wrapHtml(processedHtml));

    const fileName = `documents/${userId}/${template.name.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
    const sRef = storageRef(storage, fileName);
    await uploadBytes(sRef, pdfBlob);
    const pdfUrl = await getDownloadURL(sRef);

    const now = new Date();
    const expiryDate = new Date(now);
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);

    const docId = template.name.toLowerCase().replace(/\s+/g, '_');
    await setDoc(doc(firestore, 'users', userId, 'umowy', docId), {
      dataGeneracji: Timestamp.fromDate(now),
      dataWaznosci: Timestamp.fromDate(expiryDate),
      pdfUrl,
      signature: '',
      signed: false,
      status: 'Oczekuje na podpis',
      documentType: template.name,
      templateId: templateDoc.id,
      contractNumber: replacements.PLACEHOLDER_CONTRACT_NUMBER || '',
      createdBy: 'admin',
      version: '1.0',
    });
    return true;
  }));

  const generated = results.filter(Boolean).length;
  return { ok: generated === REQUIRED_DOCUMENTS.length, generated };
};

// Загрузка готового подписанного PDF/скана и пометка договора подписанным
export const uploadSignedContract = async ({ firestore, storage, userId, contractId, file, existingPdfUrl }) => {
  const ext = (file.name.split('.').pop() || 'pdf');
  const path = `documents/${userId}/${contractId}_signed_admin_${Date.now()}.${ext}`;
  const sRef = storageRef(storage, path);
  await uploadBytes(sRef, file);
  const url = await getDownloadURL(sRef);

  const update = {
    signed: true,
    signedAt: Timestamp.now(),
    status: 'Dokument podpisany',
    pdfUrl: url,
    signatureType: 'admin_upload',
    signedByAdmin: true,
  };
  if (existingPdfUrl) update.pdfUrlOriginal = existingPdfUrl;

  await setDoc(doc(firestore, 'users', userId, 'umowy', contractId), update, { merge: true });
  return url;
};

// Пересчёт флага allDocumentsSigned на users/{userId}
export const recomputeAllSigned = async ({ firestore, userId }) => {
  const snap = await getDocs(collection(firestore, `users/${userId}/umowy`));
  const all = snap.docs.length > 0 && snap.docs.every((d) => d.data().signed);
  if (all) {
    await setDoc(doc(firestore, 'users', userId), {
      documentsSignedAt: Timestamp.now(),
      allDocumentsSigned: true,
    }, { merge: true });
  }
  return all;
};
